// MeMeet kısayol ayarları (2026-10-14). Ayarlar penceresinin "Kısayollar" sekmesi.
// Tüm MeMeet eylemlerine + SoundPad seslerine kısayol atanır; hesapta saklanır, tüm MeMeet'lerde aynıdır.
import { useEffect, useState } from "react";
import {
  type MeMeetPrefsApi, type ShortcutAction, SHORTCUT_ACTIONS, DEFAULT_SHORTCUTS, GLOBAL_CAPABLE,
  comboFromEvent, formatCombo,
} from "../memeetPrefs";
import "./ShortcutsPanel.css";

type TranslateFn = (key: string, fallback: string, params?: Record<string, string>) => string;

const ACTION_LABELS: Record<ShortcutAction, [string, string]> = {
  toggleMic: ["memeetShortcutMic", "Mikrofonu aç / kapat"],
  toggleCamera: ["memeetShortcutCamera", "Kamerayı aç / kapat"],
  toggleSpeaker: ["memeetShortcutSpeaker", "Hoparlörü aç / kapat (kimseyi duyma)"],
  pushToTalk: ["memeetShortcutPushToTalk", "Bas-konuş (basılı tutarken mikrofon açık)"],
  toggleScreenShare: ["memeetShortcutScreen", "Ekran paylaşımını başlat / durdur"],
  toggleHand: ["memeetShortcutHand", "El kaldır / indir"],
  toggleChat: ["memeetShortcutChat", "Sohbeti aç / kapat"],
  toggleSoundpad: ["memeetShortcutSoundpad", "SoundPad'i aç / kapat"],
  openSettings: ["memeetShortcutSettings", "Ayarları aç"],
  leaveCall: ["memeetShortcutLeave", "Görüşmeden ayrıl"],
};

/** İşletim sisteminin ya da tarayıcının ayırdığı, sayfanın yakalayamadığı kısayollar */
function isReserved(combo: string, platform: string): boolean {
  const mac = ["Mod+KeyQ", "Mod+KeyW", "Mod+KeyH", "Mod+KeyM", "Mod+Tab", "Mod+Space", "Mod+KeyN", "Mod+KeyT", "Alt+Space"];
  const pc = ["Mod+KeyW", "Mod+KeyT", "Mod+KeyN", "Alt+F4", "Mod+Shift+KeyN", "Mod+Shift+KeyT", "Mod+Space", "Mod+Tab", "Meta+KeyL", "Meta+KeyD"];
  return (platform === "mac" ? mac : pc).includes(combo);
}

export function ShortcutsPanel({
  prefs, t, sounds, globalSupported,
}: {
  prefs: MeMeetPrefsApi;
  t: TranslateFn;
  sounds: Array<{ id: string; label: string }>;
  /** Masaüstü (Electron): arka planda çalışan kısayollar destekleniyor mu */
  globalSupported: boolean;
}) {
  const [capturing, setCapturing] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);
  const [soundsOpen, setSoundsOpen] = useState(false);
  const platform = prefs.platform;

  useEffect(() => {
    if (!capturing) return;
    (window as any).__memeetShortcutCapture = true;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.repeat) return;
      const plain = !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey;
      if (plain && e.code === "Escape") { setCapturing(null); return; }
      if (plain && (e.code === "Backspace" || e.code === "Delete")) { prefs.setShortcut(capturing, ""); setCapturing(null); return; }
      const combo = comboFromEvent(e, platform);
      if (!combo) return;   // yalnız değiştirici tuş — devam et
      if (isReserved(combo, platform)) { setWarn(t("memeetShortcutReserved", "Bu kısayol sistem tarafından kullanılıyor; başka bir tuş seçin.")); return; }
      setWarn(null);
      prefs.setShortcut(capturing, combo);
      setCapturing(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      (window as any).__memeetShortcutCapture = false;
    };
  }, [capturing, platform, prefs, t]);

  const row = (action: string, label: string, isSound = false) => {
    const combo = prefs.shortcuts[action] ?? "";
    const def = (DEFAULT_SHORTCUTS as Record<string, string>)[action] ?? "";
    const custom = Object.prototype.hasOwnProperty.call(prefs.prefs.shortcuts, action) && combo !== def;
    const canGlobal = globalSupported && !isSound && GLOBAL_CAPABLE.includes(action as ShortcutAction);
    return (
      <li key={action} className="memeet-sc-row">
        <span className="memeet-sc-label">{label}</span>
        <button
          type="button"
          className={`memeet-sc-key ${capturing === action ? "capturing" : ""} ${combo ? "" : "empty"}`}
          onClick={() => { setWarn(null); setCapturing(capturing === action ? null : action); }}
          aria-label={`${label}: ${combo ? formatCombo(combo, platform) : t("memeetShortcutNone", "Atanmadı")}`}
        >
          {capturing === action ? t("memeetShortcutPress", "Tuşlara basın…") : combo ? formatCombo(combo, platform) : t("memeetShortcutNone", "Atanmadı")}
        </button>
        {custom ? (
          <button type="button" className="memeet-sc-reset" onClick={() => prefs.setShortcut(action, null)} title={t("memeetShortcutResetOne", "Varsayılana dön")} aria-label={t("memeetShortcutResetOne", "Varsayılana dön")}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
          </button>
        ) : <span className="memeet-sc-reset-ph" />}
        {canGlobal && (
          <label className="memeet-sc-global" title={t("memeetShortcutGlobalHint", "Uygulama arka plandayken de çalışır")}>
            <input type="checkbox" checked={prefs.prefs.globalShortcuts.includes(action)} disabled={!combo} onChange={(e) => prefs.setGlobal(action as ShortcutAction, e.target.checked)} />
            {t("memeetShortcutGlobal", "Arka planda da")}
          </label>
        )}
      </li>
    );
  };

  return (
    <div className="device-settings-body memeet-sc">
      <p className="memeet-sc-hint">
        {capturing
          ? t("memeetShortcutCaptureHint", "Yeni kısayola basın. Esc: vazgeç, Backspace: kaldır.")
          : t("memeetShortcutHint", "Bir kısayolu değiştirmek için üzerine tıklayın. Ayarlarınız hesabınıza kaydedilir ve tüm MeMeet uygulamalarında geçerlidir.")}
        {platform === "mac" ? " " + t("memeetShortcutMacNote", "⌘ = Command, ⌥ = Option, ⇧ = Shift, ⌃ = Control.") : ""}
      </p>
      {warn && <p className="memeet-sc-warn">{warn}</p>}
      <ul className="memeet-sc-list">
        {SHORTCUT_ACTIONS.map((a) => row(a, t(ACTION_LABELS[a][0], ACTION_LABELS[a][1])))}
      </ul>
      {sounds.length > 0 && (
        <div className="memeet-sc-sounds">
          <button type="button" className="memeet-sc-sounds-toggle" aria-expanded={soundsOpen} onClick={() => setSoundsOpen((o) => !o)}>
            {t("memeetShortcutSounds", "SoundPad sesleri")} <span aria-hidden="true">{soundsOpen ? "−" : "+"}</span>
          </button>
          {soundsOpen && <ul className="memeet-sc-list">{sounds.map((s) => row("sound:" + s.id, s.label, true))}</ul>}
        </div>
      )}
      <div className="memeet-sc-footer">
        <button type="button" className="memeet-sc-resetall" onClick={() => { setCapturing(null); prefs.resetShortcuts(); }}>
          {t("memeetShortcutResetAll", "Tümünü varsayılana döndür")}
        </button>
      </div>
    </div>
  );
}
